import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, NgZone, signal } from '@angular/core';
import { Router } from '@angular/router';
import * as signalR from '@microsoft/signalr';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { ConfirmationDialog } from 'src/app/shared/confirmation-dialog/confirmation-dialog';
import { environment } from 'src/environments/environment';
import { AuthStateService } from '../authentication/auth-state-service';
import { JwtService } from '../jwt/jwt-service';

export interface AppNotification {
  id: number;
  createdAt: string;
  isRead: boolean;
  notificationType: string;
  notificationRefId: number;
  notificationRefType: string;
  url: string;
  title: string;
  body: string;
}

export interface NotificationResponse {
  data: {
    notification: AppNotification[];
    totalCount: number;
  };
  pageInfo: {
    currentPage: number;
    pageSize: number;
    totalPages: number;
    totalRecords: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private zone = inject(NgZone);
  private http = inject(HttpClient);
  private router = inject(Router);
  private dialog = inject(DialogService);

  private jwtService = inject(JwtService);
  private authStateService = inject(AuthStateService);

  private readonly notifications = signal<AppNotification[]>([]);
  private readonly notificationsData = signal<NotificationResponse | null>(null);

  notificationList = this.notifications.asReadonly();
  notification = this.notificationsData.asReadonly();

  private hubConnection!: signalR.HubConnection;

  private baseURL: string = environment.HTTP;
  private apiNotification: string = '/Notification';
  private suspendDialogRef!: DynamicDialogRef<ConfirmationDialog>;

  unreadCount = computed(() => this.notifications().filter((n) => !n.isRead).length);
  hasUnread = computed(() => this.notifications().some((n) => !n.isRead));

  //#region Connections
  public startConnection(): void {
    if (this.hubConnection && this.hubConnection.state !== signalR.HubConnectionState.Disconnected) return;

    const token = this.jwtService.getToken();
    const country = this.jwtService.getCountry();

    if (!token || !country) {
      return;
    }

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(environment.NOTIFICATION_ENDPOINT, {
        accessTokenFactory: () => this.jwtService.getToken() ?? '',
        headers: { 'X-Country': country }
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.None)
      .build();

    this.hubConnection.off('Notification');
    this.hubConnection.off('Suspend');

    this.hubConnection.on('Notification', (notification: AppNotification) => {
      this.zone.run(() => {
        this.upsertNotification(notification);
      });
    });

    this.hubConnection.on('Suspend', () => {
      this.zone.run(() => {
        this.onSuspend();
      });
    });

    this.hubConnection
      .start()
      .then(() => {
        this.retrieveNotifications();
      })
      .catch((err) => console.error('SignalR error:', err));
  }

  async stopConnection(): Promise<void> {
    if (!this.hubConnection) return;

    try {
      await this.hubConnection.stop();
    } catch (error) {
      console.error('Failed to stop SignalR:', error);
    }
  }

  ngOnDestroy(): void {
    void this.stopConnection();
  }

  //#region Notifications
  private upsertNotification(notification: AppNotification): void {
    this.notifications.update((current) => {
      const withoutExisting = current.filter((item) => item.id !== notification.id);
      return [notification, ...withoutExisting].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    });
  }

  private setNotifications(data: AppNotification[]) {
    const sorted = [...data].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    this.notifications.set(sorted);
  }

  async retrieveNotifications(pageSize = 50, pageNumber = 1, type?: string, isRead?: boolean, searchText = ''): Promise<void> {
    try {
      const response = await firstValueFrom(this.retrieveNotificationsAPI(pageSize, pageNumber, type ?? null, isRead ?? true, searchText));

      this.notificationsData.set(response);
      this.setNotifications(response?.data?.notification ?? []);
    } catch (error) {
      console.error('Failed to retrieve notifications:', error);
    }
  }

  getNotificationById(id: number) {
    return this.notifications().find((n) => n.id === id);
  }

  //#region Mark as Read
  async markAsRead(ids: number | number[]): Promise<void> {
    const notificationIds = Array.isArray(ids) ? ids : [ids];
    if (!notificationIds.length) return;
    const previous = this.notifications();
    this.notifications.update((notifications) =>
      notifications.map((notification) => (notificationIds.includes(notification.id) ? { ...notification, isRead: true } : notification))
    );

    try {
      await firstValueFrom(this.readNotifications(notificationIds));
    } catch (error) {
      this.notifications.set(previous);
      console.error('Failed to mark notifications as read:', error);
    }
  }

  //#region API Calls
  retrieveNotificationsAPI(pageSize: number, pageNumber: number, type: any, isRead: boolean, searchText: string): Observable<any> {
    let params: any = {};
    if (pageSize != null) {
      params.pageSize = pageSize;
    }

    if (pageNumber != null) {
      params.pageNumber = pageNumber;
    }

    if (type != null) {
      params.type = type;
    }

    if (isRead != null) {
      params.isRead = isRead;
    }

    if (searchText != null) {
      params.searchText = searchText;
    }

    return this.http.get<any>(`${this.baseURL + this.apiNotification}/GetUserNotification`, { params });
  }

  readNotifications(data: any): Observable<any> {
    const payload = { id: data };
    return this.http.post<any>(`${this.baseURL + this.apiNotification}/UserReadNotification`, payload);
  }

  redirectLogout() {
    // sessionStorage.clear();
    // localStorage.clear();
    // this.stopConnection();
    // this.router.navigate(['/login']);
    this.authStateService.triggerLogout();
  }

  //#region Suspend
  onSuspend() {
    this.redirectLogout();

    const dialogEvent$ = new Subject<boolean>();

    if (!this.suspendDialogRef) {
      this.suspendDialogRef = this.dialog.open(ConfirmationDialog, {
        header: '',
        data: {
          event$: dialogEvent$,
          showIcon: true,
          type: 'error',
          header: 'ACC_SUSPENDED',
          content1: 'ACC_SUSPENDED_DESC1',
          primaryBtnLabel: 'CLOSE',
          showCancel: false
        },
        width: '37.5rem',
        modal: true,
        breakpoints: {
          '960px': '75vw',
          '640px': '90vw'
        }
      });
      dialogEvent$.subscribe({
        next: () => {
          this.suspendDialogRef!.close();
        }
      });
    }
  }
}
